import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Edit, Trash2, Briefcase, MapPin } from 'lucide-react';
import { employeeAPI } from '../services/api';
import { useI18n } from '../contexts/I18nContext';
import { useToast } from '../components/Toast';
import DateInput from '../components/DateInput';
import PhoneInput from '../components/PhoneInput';
import ImprovedTable from '../components/ImprovedTable';
import PhoneLink from '../components/PhoneLink';
import Pagination from '../components/Pagination';
import { useKeyboardShortcuts, type KeyboardShortcut } from '../hooks/useKeyboardShortcuts';
import { getEmployeeRankings } from '../services/ratingApi';
import { useCompany } from '../contexts/CompanyContext';
import { useAuth } from '../contexts/AuthContext';
import { fetchCompanyUsageStats } from '../services/companyCustomizationApi';

interface Employee {
  id: string;
  name: string;
  email: string;
  phone: string;
  document: string;
  zone: string;
  hire_date: string | null;
  is_active: boolean;
  is_third_party?: boolean;
  created_at: string;
}

interface RatingHighlight {
  id: string;
  name: string;
  rating: number;
  totalRatings: number;
}

const Funcionarios: React.FC = () => {
  const { t } = useI18n();
  const { showSuccess, showError } = useToast();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [filterZone, setFilterZone] = useState('');
  const [filterActive, setFilterActive] = useState<boolean | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    document: '',
    zone: '',
    hire_date: '',
    is_third_party: false
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [ratingHighlights, setRatingHighlights] = useState<RatingHighlight[]>([]);
  const [isLoadingRatings, setIsLoadingRatings] = useState(false);
  const { currentCompany } = useCompany();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const loadEmployees = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterZone) params.append('zone', filterZone);
      if (filterActive !== null) params.append('is_active', filterActive.toString());
      
      const queryString = params.toString();
      const url = queryString ? `/employees?${queryString}` : '/employees';
      const response = await employeeAPI.get(url);
      const employeesData = response.data.employees || response.data || [];
      setEmployees(employeesData);
      setCurrentPage(1); // Reset para primeira página ao recarregar
    } catch (error: any) {
      console.error('Erro ao carregar funcionários:', error);
      showError(t('employees.error.loading') || 'Erro ao carregar funcionários');
    }
    setIsLoading(false);
  }, [filterZone, filterActive, t, showError]);

  const loadRatingHighlights = useCallback(async () => {
    setIsLoadingRatings(true);
    try {
      const response = await getEmployeeRankings({ limit: 5, min_ratings: 1 });
      let rankings: unknown[] = [];
      if (response) {
        if (Array.isArray(response.rankings)) {
          rankings = response.rankings;
        } else if (Array.isArray(response)) {
          rankings = response;
        } else if (response.rankings && typeof response.rankings === 'object') {
          rankings = Object.values(response.rankings);
        } else if (typeof response === 'object') {
          rankings = Object.values(response);
        }
      }
      const normalized: RatingHighlight[] = rankings
        .filter((item: unknown) => item != null && typeof item === 'object')
        .map((item: unknown) => {
          const obj = item as Record<string, unknown>;
          return {
            id: String(obj.employee_id || obj.employeeId || obj.id || ''),
            name: String(obj.employee_name || obj.employeeName || obj.name || ''),
            rating: Number(obj.average_rating ?? obj.rating ?? 0),
            totalRatings: Number(obj.total_ratings ?? obj.totalRatings ?? obj.completed ?? 0),
          };
        })
        .filter(item => item.id && item.name)
        .slice(0, 5);
      setRatingHighlights(normalized);
    } catch (error) {
      console.warn('Erro ao carregar ranking de avaliações:', error);
    } finally {
      setIsLoadingRatings(false);
    }
  }, []);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      document: '',
      zone: '',
      hire_date: '',
      is_third_party: false
    });
    setEditingEmployee(null);
    setShowForm(false);
  }, []);

  const createEmployee = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar campos obrigatórios
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) {
      errors.name = 'Nome é obrigatório';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email é obrigatório';
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Por favor, insira um email válido (exemplo: usuario@dominio.com)';
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showError(t('validation.fixFormErrors'));
      return;
    }
    
    setFormErrors({});
    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        hire_date: formData.hire_date || null
      };
      await employeeAPI.post('/employees', payload);
      setShowForm(false);
      resetForm();
      setFormErrors({});
      await loadEmployees();
      showSuccess(t('success') || 'Funcionário criado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao criar funcionário:', error);
      const errorMsg = error.response?.data?.error || t('error') || 'Erro ao criar funcionário';
      showError(errorMsg);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
    }
    setIsLoading(false);
  }, [formData, loadEmployees, t, resetForm, showSuccess, showError, currentCompany]);

  const updateEmployee = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEmployee) return;
    
    // Validar campos obrigatórios
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) {
      errors.name = 'Nome é obrigatório';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email é obrigatório';
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Por favor, insira um email válido (exemplo: usuario@dominio.com)';
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showError(t('validation.fixFormErrors'));
      return;
    }
    
    setFormErrors({});
    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        hire_date: formData.hire_date || null
      };
      await employeeAPI.put(`/employees/${editingEmployee.id}`, payload);
      
      // Fechar modal e limpar estado
      setEditingEmployee(null);
      resetForm();
      setFormErrors({});
      
      // Limpar estado antes de recarregar para forçar atualização
      setEmployees([]);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      await loadEmployees();
      showSuccess(t('success') || 'Funcionário atualizado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao atualizar funcionário:', error);
      const errorMsg = error.response?.data?.error || t('error') || 'Erro ao atualizar funcionário';
      showError(errorMsg);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      await loadEmployees(); // Recarregar mesmo em caso de erro
    }
    setIsLoading(false);
  }, [editingEmployee, formData, loadEmployees, t, resetForm, showSuccess, showError]);

  const deleteEmployee = useCallback(async (employeeId: string) => {
    if (!confirm(t('confirm') || 'Tem certeza que deseja desativar este funcionário?')) return;
    
    try {
      await employeeAPI.delete(`/employees/${employeeId}`);
      
      // Limpar estado antes de recarregar para forçar atualização
      setEmployees([]);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      await loadEmployees();
      showSuccess(t('success') || 'Funcionário desativado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao desativar funcionário:', error);
      const errorMsg = error.response?.data?.error || t('error') || 'Erro ao desativar funcionário';
      showError(errorMsg);
      await loadEmployees(); // Recarregar mesmo em caso de erro
    }
  }, [t, loadEmployees, showSuccess, showError]);

  const startEdit = useCallback((employee: Employee) => {
    setEditingEmployee(employee);
    // Log para debug
    console.log('🔍 Editando funcionário:', {
      id: employee.id,
      name: employee.name,
      email: employee.email,
      is_third_party: employee.is_third_party,
      raw: employee
    });
    setFormData({
      name: employee.name,
      email: employee.email,
      phone: employee.phone,
      document: employee.document,
      zone: employee.zone || '',
      hire_date: employee.hire_date ? new Date(employee.hire_date).toISOString().split('T')[0] : '',
      // Verificar tanto is_third_party quanto isThirdParty e converter corretamente
      is_third_party: Boolean(employee.is_third_party) || Boolean((employee as any).isThirdParty)
    });
    setShowForm(true);
  }, []);

  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      return;
    }
    loadEmployees();
  }, [loadEmployees, authLoading, isAuthenticated]);

  useEffect(() => {
    loadRatingHighlights();
  }, [loadRatingHighlights]);
  // Configurar atalhos de teclado
  const pageShortcuts: KeyboardShortcut[] = useMemo(() => [
    {
      key: 'c',
      ctrl: true,
      description: 'Criar novo funcionário',
      action: () => {
        if (!showForm) {
          resetForm();
          setShowForm(true);
        }
      },
      enabled: !showForm,
    },
    {
      key: 'Escape',
      description: 'Cancelar/Fechar formulário',
      action: () => {
        if (showForm) {
          resetForm();
        }
      },
      enabled: showForm,
    },
  ], [showForm, resetForm]);

  useKeyboardShortcuts(pageShortcuts, { ignoreInputs: true });

  // Obter zonas únicas para filtro
  const uniqueZones = useMemo(() => 
    Array.from(new Set(employees.map(e => e.zone).filter(Boolean))),
    [employees]
  );

  // Memoizar colunas para evitar recriação a cada render
  const columns = useMemo(() => {
    // Extrair todas as traduções de uma vez para evitar recálculos
    const nameLabel = t('employees.name') || 'Nome';
    const emailLabel = t('employees.email') || 'Email';
    const phoneLabel = t('employees.phone') || 'Telefone';
    const zoneLabel = t('employees.zone') || 'Zona';
    const statusLabel = t('employees.status') || 'Status';
    const actionsLabel = t('actions') || 'Ações';
    const editLabel = t('edit') || 'Editar';
    const deleteLabel = t('delete') || 'Desativar';
    const activeLabel = t('employees.active') || 'Ativo';
    const inactiveLabel = t('employees.inactive') || 'Inativo';
    const noDocumentLabel = t('employees.noDocument') || 'Sem documento';
    
    return [
      {
        key: 'name',
        label: nameLabel,
        width: '20%',
        minWidth: '180px',
        render: (employee: Employee) => (
          <div>
            <div style={{ fontWeight: '500', marginBottom: '4px' }}>{employee.name}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {employee.document || noDocumentLabel}
            </div>
          </div>
        ),
      },
      {
        key: 'email',
        label: emailLabel,
        width: '18%',
        minWidth: '150px',
        collapsible: true,
      },
      {
        key: 'phone',
        label: phoneLabel,
        width: '12%',
        minWidth: '120px',
        collapsible: true,
        render: (employee: Employee) => (
          <PhoneLink phone={employee.phone || ''} showIcon={false} />
        ),
      },
      {
        key: 'zone',
        label: zoneLabel,
        width: '15%',
        minWidth: '120px',
        collapsible: true,
        render: (employee: Employee) =>
          employee.zone ? (
            <span
              style={{
                backgroundColor: '#e0e7ff',
                color: '#4338ca',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '500',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <MapPin size={12} />
              {employee.zone}
            </span>
          ) : (
            <span style={{ color: '#999', fontSize: '14px' }}>-</span>
          ),
      },
      {
        key: 'status',
        label: statusLabel,
        width: '15%',
        minWidth: '140px',
        collapsible: true,
        render: (employee: Employee) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <span
              style={{
                backgroundColor: employee.is_active ? '#d1fae5' : '#fee2e2',
                color: employee.is_active ? '#065f46' : '#991b1b',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500',
                display: 'inline-block',
                width: 'fit-content',
              }}
            >
              {employee.is_active ? activeLabel : inactiveLabel}
            </span>
            {employee.is_third_party && (
              <span
                style={{
                  backgroundColor: '#fef3c7',
                  color: '#92400e',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  display: 'inline-block',
                  width: 'fit-content',
                }}
              >
                Terceirizado
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'actions',
        label: actionsLabel,
        width: '20%',
        minWidth: '180px',
        render: (employee: Employee) => (
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => startEdit(employee)}
              style={{
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '13px',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2563eb';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3b82f6';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <Edit size={14} />
              {editLabel}
            </button>
            {employee.is_active && (
              <button
                onClick={() => deleteEmployee(employee.id)}
                style={{
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '13px',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#dc2626';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ef4444';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <Trash2 size={14} />
                {deleteLabel}
              </button>
            )}
          </div>
        ),
      },
    ];
  }, [t, startEdit, deleteEmployee]);

  // Memoizar dados paginados
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return employees.slice(startIndex, endIndex);
  }, [employees, currentPage, itemsPerPage]);

  return (
    <div style={{ padding: '30px' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: '0 0 10px 0', color: '#333' }}>
          {t('employees.title') || 'Gestão de Funcionários'}
        </h1>
        <p style={{ color: '#666', margin: 0 }}>
          {t('employees.subtitle') || 'Gerencie a equipe de funcionários'}
        </p>
      </div>

      {(isLoadingRatings || ratingHighlights.length > 0) && (
        <div
          style={{
            backgroundColor: '#f5f3ff',
            border: '1px solid #c4b5fd',
            borderRadius: '10px',
            padding: '20px',
            marginBottom: '20px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '20px',
            alignItems: 'center',
          }}
        >
          <div style={{ flex: 1, minWidth: '220px' }}>
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#4c1d95', marginBottom: '6px' }}>
              {t('employees.ratings.highlightTitle') || 'Destaques em avaliações'}
            </div>
            <p style={{ margin: 0, color: '#6b21a8', fontSize: '14px' }}>
              {t('employees.ratings.highlightSubtitle') ||
                'Acompanhe quem está recebendo as melhores notas dos clientes.'}
            </p>
          </div>
          <div style={{ flex: 2, minWidth: '280px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {isLoadingRatings ? (
              <span style={{ color: '#6b21a8' }}>{t('loading') || 'Carregando avaliações...'}</span>
            ) : (
              ratingHighlights.map((highlight, index) => (
                <div
                  key={highlight.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '10px 14px',
                    boxShadow: '0 1px 2px rgba(15,23,42,0.1)',
                  }}
                >
                  <span style={{ fontWeight: 700, color: '#7c3aed', minWidth: '24px' }}>#{index + 1}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#1f2937' }}>{highlight.name}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      ⭐ {highlight.rating.toFixed(1)} • {highlight.totalRatings}{' '}
                      {t('employees.ratings.total') || 'avaliações'}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '20px',
        marginBottom: '20px'
      }}>
        {/* Filtros */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '15px',
          marginBottom: '20px'
        }}>
          <div>
            <label htmlFor="employee-filter-zone" style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
              {t('employees.filter.zone') || 'Zona'}
            </label>
            <select
              id="employee-filter-zone"
              name="filter-zone"
              value={filterZone}
              onChange={(e) => setFilterZone(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="">{t('employees.filter.allZones') || 'Todas as zonas'}</option>
              {uniqueZones.map(zone => (
                <option key={zone} value={zone}>{zone}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="employee-filter-status" style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
              {t('employees.filter.status') || 'Status'}
            </label>
            <select
              id="employee-filter-status"
              name="filter-status"
              value={filterActive === null ? '' : filterActive.toString()}
              onChange={(e) => setFilterActive(e.target.value === '' ? null : e.target.value === 'true')}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="">{t('employees.filter.allStatus') || 'Todos'}</option>
              <option value="true">{t('employees.filter.active') || 'Ativos'}</option>
              <option value="false">{t('employees.filter.inactive') || 'Inativos'}</option>
            </select>
          </div>
        </div>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: 0, color: '#333' }}>
            {t('employees.list') || 'Lista de Funcionários'} ({employees.length})
          </h3>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            style={{
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: '500'
            }}
          >
            <Plus size={20} />
            {t('employees.new') || 'Novo Funcionário'}
          </button>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>{t('employees.loading') || 'Carregando funcionários...'}</div>
          </div>
        ) : employees.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <Briefcase size={48} style={{ marginBottom: '10px', opacity: 0.5 }} />
            <div>{t('employees.notFound') || 'Nenhum funcionário encontrado'}</div>
          </div>
        ) : (
          <>
            <ImprovedTable
            columns={columns}
            data={paginatedData}
            keyField="id"
            striped={true}
            hoverable={true}
            compactable={true}
            defaultCollapsed={[]}
          />
          {employees.length > itemsPerPage && (
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(employees.length / itemsPerPage)}
              totalItems={employees.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(newItemsPerPage) => {
                setItemsPerPage(newItemsPerPage);
                setCurrentPage(1);
              }}
              showItemsPerPage={true}
            />
          )}
          </>
        )}
      </div>

      {/* Modal de Formulário */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>
              {editingEmployee 
                ? (t('employees.edit') || 'Editar Funcionário') 
                : (t('employees.new') || 'Novo Funcionário')}
            </h2>

            <form onSubmit={editingEmployee ? updateEmployee : createEmployee}>
              <div style={{ marginBottom: '15px' }}>
                <label htmlFor="employee-name" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  {t('employees.name') || 'Nome'} <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  id="employee-name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    if (formErrors.name) {
                      setFormErrors({...formErrors, name: ''});
                    }
                  }}
                  autoComplete="name"
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1px solid ${formErrors.name ? '#dc2626' : '#ddd'}`,
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                {formErrors.name && (
                  <div style={{ marginTop: '4px', fontSize: '12px', color: '#dc2626' }}>
                    ⚠️ {formErrors.name}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label htmlFor="employee-email" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  {t('employees.email') || 'Email'} <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  id="employee-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    if (formErrors.email) {
                      setFormErrors({...formErrors, email: ''});
                    }
                  }}
                  autoComplete="email"
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `1px solid ${formErrors.email ? '#dc2626' : '#ddd'}`,
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                {formErrors.email && (
                  <div style={{ marginTop: '4px', fontSize: '12px', color: '#dc2626' }}>
                    ⚠️ {formErrors.email}
                  </div>
                )}
              </div>

              <PhoneInput
                label={t('employees.phone') || 'Telefone'}
                value={formData.phone}
                onChange={(value) => {
                  setFormData({ ...formData, phone: value });
                  if (formErrors.phone) {
                    setFormErrors({...formErrors, phone: ''});
                  }
                }}
              />

              <div style={{ marginBottom: '15px' }}>
                <label htmlFor="employee-document" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  {t('employees.document') || 'Documento'}
                </label>
                <input
                  id="employee-document"
                  name="document"
                  type="text"
                  value={formData.document}
                  onChange={(e) => setFormData({ ...formData, document: e.target.value })}
                  autoComplete="off"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label htmlFor="employee-zone" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  {t('employees.zone') || 'Zona de Atuação'}
                </label>
                <input
                  id="employee-zone"
                  name="zone"
                  type="text"
                  value={formData.zone}
                  onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                  placeholder={t('employees.zoneExample')}
                  autoComplete="off"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <DateInput
                label={t('employees.hireDate') || 'Data de Contratação'}
                value={formData.hire_date || ''}
                onChange={(value) => {
                  setFormData({ ...formData, hire_date: value });
                  if (formErrors.hire_date) {
                    setFormErrors({...formErrors, hire_date: ''});
                  }
                }}
                placeholder={t('common.dateFormatHint')}
              />

              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="employee-third-party" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input
                    id="employee-third-party"
                    name="is_third_party"
                    type="checkbox"
                    checked={formData.is_third_party}
                    onChange={(e) => setFormData({ ...formData, is_third_party: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: '500' }}>
                    Terceirizado
                  </span>
                </label>
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px', marginLeft: '28px' }}>{t('employees.thirdPartyHint')}</p>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: 'white',
                    color: '#333'
                  }}
                >
                  {t('cancel') || 'Cancelar'}
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    backgroundColor: '#10b981',
                    color: 'white',
                    opacity: isLoading ? 0.6 : 1
                  }}
                >
                  {isLoading 
                    ? (t('saving') || 'Salvando...') 
                    : (t('save') || 'Salvar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Funcionarios;

